/**
 * Pure formatting for the Survivor Game tab's status bar: Status, Submitted,
 * Lock and the missed pick note (S5). Never reads a clock; `nowMs` always
 * comes from the caller.
 */
import {
  SURVIVOR_LOCKED_VALUE,
  SURVIVOR_MISSED_PICK_NOTE,
  SURVIVOR_STATUS_ELIMINATED,
  SURVIVOR_STATUS_IN,
  SURVIVOR_STATUS_NOT_PLAYING,
  SURVIVOR_STATUS_REVIVED,
  SURVIVOR_UNDER_A_MINUTE,
  survivorFromNowLabel,
} from "./survivorGameCopy";

export type typeEntryStatusValue = "in" | "eliminated" | "revived";
export type typeStatusTone = "success" | "accent" | "error" | "neutral";
export const STATUS_TONE_CLASS: Record<typeStatusTone, string> = {
  success: "text-success",
  accent: "text-accent",
  error: "text-error",
  neutral: "text-ink-secondary",
};

export function formatEntryStatus(status: typeEntryStatusValue | null): {
  label: string;
  tone: typeStatusTone;
} {
  switch (status) {
    case "in":
      return { label: SURVIVOR_STATUS_IN, tone: "success" };
    case "revived":
      return { label: SURVIVOR_STATUS_REVIVED, tone: "accent" };
    case "eliminated":
      return { label: SURVIVOR_STATUS_ELIMINATED, tone: "error" };
    case null:
      return { label: SURVIVOR_STATUS_NOT_PLAYING, tone: "neutral" };
  }
}

export function formatSubmittedLabel(input: {
  savedCount: number;
  requiredCount: number;
}): string {
  return `${input.savedCount}/${input.requiredCount}`;
}

export function formatLockCountdown(input: {
  locksAtIso: string | null;
  noEligibleGames?: boolean;
  nowMs: number;
  isLocked: boolean;
}): string {
  if (input.locksAtIso === null) {
    return input.noEligibleGames ? "No eligible games" : "Deadline unavailable";
  }
  if (input.isLocked) return SURVIVOR_LOCKED_VALUE;
  const locksAtMs = Date.parse(input.locksAtIso);
  if (Number.isNaN(locksAtMs)) return "";
  const remaining = locksAtMs - input.nowMs;
  if (remaining <= 0) return SURVIVOR_LOCKED_VALUE;
  if (remaining < 60_000) return SURVIVOR_UNDER_A_MINUTE;
  if (remaining < 3_600_000) {
    return survivorFromNowLabel(Math.floor(remaining / 60_000), "minute");
  }
  if (remaining < 86_400_000) {
    return survivorFromNowLabel(Math.floor(remaining / 3_600_000), "hour");
  }
  return survivorFromNowLabel(Math.floor(remaining / 86_400_000), "day");
}

export function resolveMissedPickNote(input: {
  isOpen: boolean;
  savedCount: number;
  requiredCount: number;
}): string | null {
  return input.isOpen && input.savedCount < input.requiredCount
    ? SURVIVOR_MISSED_PICK_NOTE
    : null;
}
